<template><div><h2 id="introduction" tabindex="-1" class="d-docsite--header-2">Introduction <a class="header-anchor d-link d-docsite--link d-link" href="#introduction" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Utilities – also known as &quot;trumps,&quot; &quot;helper classes,&quot; or &quot;alterations&quot; – are high-specificity selectors for making very targeted alterations to existing elements or components.</p>
<p class="d-docsite--paragraph">Each utility is a small, <a class="d-link" href="https://css-tricks.com/lets-define-exactly-atomic-css/" target="_blank">atomic style</a> declaration that, when chained together, should mitigate most situations in which custom CSS must be written. Just write these classes right in your mark-up and you're all set!</p>
<p class="d-docsite--paragraph">Most utilities come with responsive options, enabling alterations at targeted screen size ranges. For example, &quot;small padding on smaller screens, larger padding on bigger screens.&quot;</p>
<p class="d-docsite--paragraph">While an atomic CSS approach comes with many advantages, we recognize it also offers a notable disadvantage: reducing the CSS cascade. This is especially true for repeated UI elements, which can end up creating redundant mark-up. For these instances, Dialtone offers components.</p>
<h2 id="example" tabindex="-1" class="d-docsite--header-2">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example" aria-hidden="true">#</a></h2>
<h3 id="border-top" tabindex="-1" class="d-docsite--header-3">Border Top <a class="header-anchor d-link d-docsite--link d-link" href="#border-top" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>d-bt</code> applies a border (<code v-pre>b</code>) to the top (<code v-pre>t</code>) side. 1px is the default width, and the color is inherited from the foreground color (<code v-pre>currentColor</code>).</p>
<code-example source-code='&lt;div class=&quot;d-bt&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bt">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="border-top-width" tabindex="-1" class="d-docsite--header-3">Border Top Width <a class="header-anchor d-link d-docsite--link d-link" href="#border-top-width" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>d-btw4</code> applies a 4px (<code v-pre>4</code>) width (<code v-pre>w</code>) to the top (<code v-pre>t</code>).</p>
<code-example source-code='&lt;div class=&quot;d-bt d-btw4&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bt d-btw4">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="border-color" tabindex="-1" class="d-docsite--header-3">Border Color <a class="header-anchor d-link d-docsite--link d-link" href="#border-color" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph"><code v-pre>d-bc-critical</code> applies a <RouteLink to="/design/colors/#borders" class="d-docsite--link d-link">critical</RouteLink> border (<code v-pre>b</code>) color (<code v-pre>c</code>).</p>
<code-example source-code='&lt;div class=&quot;d-bt d-btw4 d-bc-critical&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bt d-btw4 d-bc-critical">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h2 id="tutorial" tabindex="-1" class="d-docsite--header-2">Tutorial <a class="header-anchor d-link d-docsite--link d-link" href="#tutorial" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A basic example styling a container by combining Dialtone's CSS utilities. Follow each step with this <a href="https://codepen.io/pen?template=dyKvvZB" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Codepen template.</a></p>
<h3 id="_1-begin-with-an-unstyled-container" tabindex="-1" class="d-docsite--header-3">1. Begin with an Unstyled Container <a class="header-anchor d-link d-docsite--link d-link" href="#_1-begin-with-an-unstyled-container" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;div&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div>
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="_2-apply-an-inverted-background-color" tabindex="-1" class="d-docsite--header-3">2. Apply an Inverted Background Color <a class="header-anchor d-link d-docsite--link d-link" href="#_2-apply-an-inverted-background-color" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Apply a <RouteLink to="/utilities/backgrounds/color/#classes" class="d-docsite--link d-link"><strong>Primary Inverted</strong> Surface Color</RouteLink>.</p>
<code-example source-code='&lt;div class=&quot;d-bgc-primary-inverted&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bgc-primary-inverted">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="_3-apply-color-to-the-foreground-text" tabindex="-1" class="d-docsite--header-3">3. Apply Color to the Foreground Text <a class="header-anchor d-link d-docsite--link d-link" href="#_3-apply-color-to-the-foreground-text" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Since this will be primary content on a dark surface, let's use <code v-pre>d-fc-primary-inverted</code> from the <RouteLink to="/utilities/typography/color/" class="d-docsite--link d-link">font color utilities</RouteLink>.</p>
<code-example source-code='&lt;div class=&quot;d-bgc-primary-inverted d-fc-primary-inverted&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bgc-primary-inverted d-fc-primary-inverted">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="_4-apply-some-padding" tabindex="-1" class="d-docsite--header-3">4. Apply Some Padding <a class="header-anchor d-link d-docsite--link d-link" href="#_4-apply-some-padding" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Let's use <code v-pre>d-p-100</code> padding for all four sides, from the list of <RouteLink to="/utilities/spacing/padding/" class="d-docsite--link d-link">padding utility classes</RouteLink>.</p>
<code-example source-code='&lt;div class=&quot;d-bgc-primary-inverted d-fc-primary-inverted d-p-100&quot;&gt;
  &lt;dt-text&gt;Box&lt;/dt-text&gt;
&lt;/div&gt;'>
<div class="d-bgc-primary-inverted d-fc-primary-inverted d-p-100">
  <dt-text>Box</dt-text>
</div>
</code-example>
<h3 id="_5-list-a-bunch-of-boxes" tabindex="-1" class="d-docsite--header-3">5. List a Bunch of Boxes <a class="header-anchor d-link d-docsite--link d-link" href="#_5-list-a-bunch-of-boxes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Let's repeat them in a <RouteLink to="/components/stack.html" class="d-docsite--link d-link">Stack</RouteLink> component. Note that I've moved the surface and foreground colors to the parent container so they may inherit.</p>
<code-example source-code='&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box 1&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box the 2nd&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box third&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box IV&lt;/dt-text&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper class="d-bgc-primary-inverted d-fc-primary-inverted">
  <div class="d-p-100">
    <dt-text>Box 1</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box the 2nd</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box third</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box IV</dt-text>
  </div>
</dt-stack>
</code-example>
<h3 id="_6-render-them-horizontally" tabindex="-1" class="d-docsite--header-3">6. Render Them Horizontally <a class="header-anchor d-link d-docsite--link d-link" href="#_6-render-them-horizontally" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Let's add the <code v-pre>direction</code> prop to make them flow horizontally.</p>
<code-example source-code='&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box 1&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box the 2nd&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box third&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box IV&lt;/dt-text&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper direction="row" class="d-bgc-primary-inverted d-fc-primary-inverted">
  <div class="d-p-100">
    <dt-text>Box 1</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box the 2nd</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box third</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box IV</dt-text>
  </div>
</dt-stack>
</code-example>
<h3 id="_7-add-borders-to-segment-each" tabindex="-1" class="d-docsite--header-3">7. Add Borders to Segment Each <a class="header-anchor d-link d-docsite--link d-link" href="#_7-add-borders-to-segment-each" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add a border between each item with <code v-pre>d-divide-x</code>. Its default color is <code v-pre>currentColor</code>.</p>
<code-example source-code='&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box 1&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box the 2nd&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box third&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box IV&lt;/dt-text&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper direction="row" class="d-bgc-primary-inverted d-fc-primary-inverted d-divide-x">
  <div class="d-p-100">
    <dt-text>Box 1</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box the 2nd</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box third</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box IV</dt-text>
  </div>
</dt-stack>
</code-example>
<h3 id="_8-and-change-the-border-color" tabindex="-1" class="d-docsite--header-3">8. And Change the Border Color <a class="header-anchor d-link d-docsite--link d-link" href="#_8-and-change-the-border-color" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Since the border color inherits the color of the parent's foreground (implicitly <code v-pre>currentColor</code>), let's soften it with a bold inverted border color <code v-pre>d-divide-moderate-inverted</code>.</p>
<code-example source-code='&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box 1&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box the 2nd&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box third&lt;/dt-text&gt;
&lt;/div&gt;
&lt;div class=&quot;d-p-100&quot;&gt;
  &lt;dt-text&gt;Box IV&lt;/dt-text&gt;
&lt;/div&gt;'>
<dt-stack data-demo-wrapper direction="row" class="d-bgc-primary-inverted d-fc-primary-inverted d-divide-x d-divide-moderate-inverted">
  <div class="d-p-100">
    <dt-text>Box 1</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box the 2nd</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box third</dt-text>
  </div>
  <div class="d-p-100">
    <dt-text>Box IV</dt-text>
  </div>
</dt-stack>
</code-example>
<h3 id="_9-how-about-some-icons" tabindex="-1" class="d-docsite--header-3">9. How about some icons <a class="header-anchor d-link d-docsite--link d-link" href="#_9-how-about-some-icons" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add some <RouteLink to="/design/icons/" class="d-docsite--link d-link">icons</RouteLink>.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-triangle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Box 1&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;info&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Box the 2nd&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Box third&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;check-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Box IV&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper direction="row" class="d-bgc-primary-inverted d-fc-primary-inverted d-divide-x d-divide-moderate-inverted">
  <dt-stack direction="row" gap="100" class="d-p-100">
    <dt-icon name="alert-triangle" size="200" />
    <dt-text>Box 1</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-p-100">
    <dt-icon name="info" size="200" />
    <dt-text>Box the 2nd</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-p-100">
    <dt-icon name="alert-circle" size="200" />
    <dt-text>Box third</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-p-100">
    <dt-icon name="check-circle" size="200" />
    <dt-text>Box IV</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="_10-let-s-use-some-real-color" tabindex="-1" class="d-docsite--header-3">10. Let's use some real color <a class="header-anchor d-link d-docsite--link d-link" href="#_10-let-s-use-some-real-color" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Apply a <RouteLink to="/utilities/backgrounds/color.html" class="d-docsite--link d-link">semantic surface color</RouteLink> to convey some meaning to them, e.g. <code v-pre>d-bgc-critical-strong</code>.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-critical-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-triangle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Critical&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-info-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Info&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-success-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;check-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text&gt;Success&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100" class="d-fc-primary-inverted">
  <dt-stack direction="row" gap="100" class="d-bgc-critical-strong d-p-100">
    <dt-icon name="alert-triangle" size="200" />
    <dt-text>Critical</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-bgc-info-strong d-p-100">
    <dt-icon name="alert-circle" size="200" />
    <dt-text>Info</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-bgc-success-strong d-p-100">
    <dt-icon name="check-circle" size="200" />
    <dt-text>Success</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="_11-apply-a-text-style" tabindex="-1" class="d-docsite--header-3">11. Apply a text style <a class="header-anchor d-link d-docsite--link d-link" href="#_11-apply-a-text-style" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use the <a href="/components/text.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtText</a> component for text styling. It provides a semantic, prop-driven API for typography.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-critical-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-triangle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Critical&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-info-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Info&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;100&quot; class=&quot;d-bgc-success-strong d-p-100&quot;&gt;
  &lt;dt-icon name=&quot;check-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Success&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100" class="d-fc-primary-inverted">
  <dt-stack direction="row" gap="100" class="d-bgc-critical-strong d-p-100">
    <dt-icon name="alert-triangle" size="200" />
    <dt-text kind="label" :size="200">Critical</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-bgc-info-strong d-p-100">
    <dt-icon name="alert-circle" size="200" />
    <dt-text kind="label" :size="200">Info</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="100" class="d-bgc-success-strong d-p-100">
    <dt-icon name="check-circle" size="200" />
    <dt-text kind="label" :size="200">Success</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="_12-tweak-the-spacing" tabindex="-1" class="d-docsite--header-3">12. Tweak the spacing <a class="header-anchor d-link d-docsite--link d-link" href="#_12-tweak-the-spacing" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Refine the spacing by adjusting the <RouteLink to="/components/stack.html" class="d-docsite--link d-link">Stack</RouteLink> <code v-pre>gap</code> prop and padding utilities for horizontal and vertical padding.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bgc-critical-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-triangle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Critical&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bgc-info-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Info&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bgc-success-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;check-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Success&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100" class="d-fc-primary-inverted">
  <dt-stack direction="row" gap="50" class="d-bgc-critical-strong d-py-50 d-px-100">
    <dt-icon name="alert-triangle" size="200" />
    <dt-text kind="label" :size="200">Critical</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="50" class="d-bgc-info-strong d-py-50 d-px-100">
    <dt-icon name="alert-circle" size="200" />
    <dt-text kind="label" :size="200">Info</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="50" class="d-bgc-success-strong d-py-50 d-px-100">
    <dt-icon name="check-circle" size="200" />
    <dt-text kind="label" :size="200">Success</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="_13-round-it-out" tabindex="-1" class="d-docsite--header-3">13. Round it out! <a class="header-anchor d-link d-docsite--link d-link" href="#_13-round-it-out" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Add <code v-pre>d-bar-300</code> to each item for subtle rounded corners.</p>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bar-300 d-bgc-critical-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-triangle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Critical&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bar-300 d-bgc-info-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;alert-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Info&lt;/dt-text&gt;
&lt;/dt-stack&gt;
&lt;dt-stack direction=&quot;row&quot; gap=&quot;50&quot; class=&quot;d-bar-300 d-bgc-success-strong d-py-50 d-px-100&quot;&gt;
  &lt;dt-icon name=&quot;check-circle&quot; size=&quot;200&quot; /&gt;
  &lt;dt-text kind=&quot;label&quot; :size=&quot;200&quot;&gt;Success&lt;/dt-text&gt;
&lt;/dt-stack&gt;'>
<dt-stack data-demo-wrapper direction="row" gap="100" class="d-fc-primary-inverted">
  <dt-stack direction="row" gap="50" class="d-bar-300 d-bgc-critical-strong d-py-50 d-px-100">
    <dt-icon name="alert-triangle" size="200" />
    <dt-text kind="label" :size="200">Critical</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="50" class="d-bar-300 d-bgc-info-strong d-py-50 d-px-100">
    <dt-icon name="alert-circle" size="200" />
    <dt-text kind="label" :size="200">Info</dt-text>
  </dt-stack>
  <dt-stack direction="row" gap="50" class="d-bar-300 d-bgc-success-strong d-py-50 d-px-100">
    <dt-icon name="check-circle" size="200" />
    <dt-text kind="label" :size="200">Success</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<h3 id="keep-exploring" tabindex="-1" class="d-docsite--header-3">Keep Exploring! <a class="header-anchor d-link d-docsite--link d-link" href="#keep-exploring" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">View the final result in this <a href="https://codepen.io/pen?template=XWYMMRY" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Codepen template</a>. Continue to explore using the variety of CSS utilities listed here.</p>
</div></template>


